import { Button, Stack, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import type { Account } from '@/api/accounts';
import { MoneyText } from '@/components/MoneyText';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';

interface OpeningBalanceRowProps {
  account: Account;
  /** The account's own currency (bank-side, MM-D-63). */
  currency: string | null;
  /** Opens the edit form at the opening field; absent for a viewer who can't change it. */
  onEdit?: (() => void) | undefined;
  /** Confirms the served opening (`POST …/opening/confirm`). Absent once it is already confirmed. */
  onConfirm?: (() => void) | undefined;
  confirming?: boolean | undefined;
}

/**
 * The account's starting balance and where it came from (TMMAPP-0123 items 16 and 19): "Starting
 * balance {amount} on {date} · you entered it" (from your file, from your file's running balance,
 * from your bank connection), or, while pending, "Not set yet" with Set starting balance.
 */
export function OpeningBalanceRow({
  account,
  currency,
  onEdit,
  onConfirm,
  confirming = false,
}: OpeningBalanceRowProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const pending = account.openingPending;
  const set = !pending && account.openingBalanceMinor !== null && account.openingBalanceOn !== null;
  if (!pending && !set) return null;
  return (
    <Stack spacing={1} data-opening-row="">
      <Stack
        direction="row"
        sx={{ alignItems: 'center', flexWrap: 'wrap', columnGap: 1.5, rowGap: 0.5 }}
      >
        <Typography variant="body2">
          {set ? (
            <Trans
              i18nKey={
                account.openingSource !== null
                  ? 'accounts.openingSetWithSource'
                  : 'accounts.openingSet'
              }
              values={{
                date: formatCivilDate(account.openingBalanceOn!, locale),
                ...(account.openingSource !== null
                  ? { source: t(`accounts.openingSource.${account.openingSource}`) }
                  : {}),
              }}
              components={{
                amount: (
                  <MoneyText
                    minor={account.openingBalanceMinor}
                    currency={currency}
                    variant="body2"
                  />
                ),
              }}
            />
          ) : (
            t('accounts.openingNotSet')
          )}
        </Typography>
        {onConfirm !== undefined && (
          <Button size="small" onClick={onConfirm} disabled={confirming}>
            {t('accounts.looksRight')}
          </Button>
        )}
        {onEdit !== undefined && (
          <Button size="small" onClick={onEdit} disabled={confirming}>
            {t(pending ? 'accounts.setOpening' : 'accounts.editOpening')}
          </Button>
        )}
      </Stack>
    </Stack>
  );
}
